import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Sparkles } from "lucide-react";
import { track } from "@/lib/analytics";
import { pickVariant, exposeVariant, recordConversion } from "@/lib/experiments";

/**
 * Shared CTA block + disclaimers + related articles + "email me my results"
 * row used by every tool's result page.
 */

// Variants for the primary "upload" CTA — copy only. Bucketed deterministically
// per anonymous ID so we don't flicker between renders. Winner picked off the
// PostHog `tool_cta_experiment_exposed` + `tool_upload_cta_clicked` funnel.
const UPLOAD_CTA_COPY = {
    control: "Upload your inspection report",
    specific: "Analyze my inspection report",
    timed: "Get my full report in 60 seconds",
};
const UPLOAD_CTA_VARIANTS = ["control", "specific", "timed"];

export function PrimaryCtaBlock({ toolSlug, includeClosingCta = false }) {
    const variant = pickVariant("cta_upload_v1", UPLOAD_CTA_VARIANTS);
    exposeVariant("cta_upload_v1", variant, { tool_name: toolSlug });
    const uploadLabel = UPLOAD_CTA_COPY[variant] || UPLOAD_CTA_COPY.control;
    return (
        <div
            className="bl-card p-6 sm:p-8 border-2 border-bl-primary/30 bg-[var(--bl-teal-tint)]"
            data-testid="tool-cta-block"
        >
            <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-bl-primary mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-semibold text-xl text-bl tracking-tight">
                        Want report-specific guidance?
                    </h3>
                    <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                        Get a report-specific analysis with prioritized issues,
                        repair exposure, and negotiation guidance — built from
                        your actual inspection PDF.
                    </p>
                </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Link
                    to="/upload"
                    onClick={() => {
                        track("tool_upload_cta_clicked", {
                            tool_name: toolSlug,
                            experiment_key: "cta_upload_v1",
                            variant,
                        });
                        recordConversion(
                            "cta_upload_v1",
                            variant,
                            "upload_cta_clicked",
                            { tool_name: toolSlug },
                        );
                    }}
                    className="bl-btn-primary justify-center gap-2"
                    data-testid="tool-cta-upload"
                >
                    <FileText className="h-4 w-4" />
                    {uploadLabel}
                </Link>
                <Link
                    to="/sample-analysis"
                    onClick={() =>
                        track("tool_sample_report_clicked", {
                            tool_name: toolSlug,
                        })
                    }
                    className="bl-btn-secondary justify-center gap-2"
                    data-testid="tool-cta-sample"
                >
                    View sample report
                    <ArrowUpRight className="h-4 w-4" />
                </Link>
            </div>

            {includeClosingCta && (
                <p className="mt-4 text-xs text-bl-muted">
                    Under contract?{" "}
                    <Link
                        to="/pricing"
                        className="underline underline-offset-4"
                    >
                        Closing Strategy
                    </Link>{" "}
                    adds full negotiation framing, seller-credit guidance, and a
                    deadline-aware plan.
                </p>
            )}
        </div>
    );
}

export function DisclaimerList({ disclaimers }) {
    if (!disclaimers?.length) return null;
    return (
        <div
            className="rounded-lg border border-bl bg-bl-surface p-5 text-xs leading-relaxed text-bl-muted space-y-2"
            data-testid="tool-disclaimers"
        >
            {disclaimers.map((d, i) => (
                <p key={i}>{d}</p>
            ))}
        </div>
    );
}

export function RelatedArticles({ slugs = [], toolSlug }) {
    if (!slugs?.length) return null;
    return (
        <div data-testid="tool-related-articles">
            <h4 className="font-display font-semibold text-base text-bl mb-3">
                Related reading
            </h4>
            <ul className="space-y-2">
                {slugs.map((s) => (
                    <li key={s}>
                        <Link
                            to={`/learn/${s}`}
                            onClick={() =>
                                track("tool_related_article_clicked", {
                                    tool_name: toolSlug,
                                    article_slug: s,
                                })
                            }
                            className="inline-flex items-center gap-1.5 text-sm text-bl-primary hover:underline underline-offset-4"
                        >
                            {humanizeSlug(s)}
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function humanizeSlug(slug) {
    return slug
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

export function CostSafeNote() {
    return (
        <p
            className="text-xs text-bl-muted italic leading-relaxed"
            data-testid="tool-cost-safe-note"
        >
            This free tool uses rule-based guidance and does not analyze your
            full inspection report.
        </p>
    );
}

export function ConcernBadge({ level }) {
    const palette = {
        low: "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)] border-[var(--bl-moss)]/30",
        moderate:
            "bg-[var(--bl-amber-tint)] text-[var(--bl-amber)] border-[var(--bl-amber)]/30",
        high: "bg-[var(--bl-rust-tint)] text-[var(--bl-rust)] border-[var(--bl-rust)]/30",
    };
    const label = {
        low: "Low concern",
        moderate: "Moderate concern",
        high: "High concern",
    };
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold tracking-wide uppercase ${palette[level] || palette.low}`}
            data-testid={`concern-badge-${level}`}
        >
            {label[level] || "Result"}
        </span>
    );
}
